import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Empty, Input, Row, Segmented, Select, Space, Spin, Statistic, Table, Tag, Tooltip, Typography, message } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { accountsApi, type AccountBlockEvent, type AccountStatsRow, type AccountsStatistics as StatsPayload } from '../services/api'
import AccountsCharts from './AccountsCharts'

const { Text, Title } = Typography

// Наши коды блокировок - те же, что ставит разбор ошибок Telegram.
const BLOCK_LABELS: Record<string, string> = {
    flood_wait: 'Флуд-ожидание',
    flood_premium_wait: 'Флуд (нужен premium)',
    slowmode_wait: 'Медленный режим',
    flood_test_phone_wait: 'Флуд (тестовый номер)',
    two_factor_confirm_wait: 'Ожидание подтверждения 2FA',
    password_too_fresh: 'Пароль слишком свежий',
    session_too_fresh: 'Сессия слишком свежая',
    takeout_init_delay: 'Задержка выгрузки',
    connection_retry: 'Сбой подключения',
    spam_block: 'Спам-блок',
    peer_flood: 'Ограничение на новые чаты',
    banned: 'Аккаунт заблокирован',
    deactivated: 'Аккаунт удалён',
    auth_key_invalid: 'Сессия недействительна',
    unknown: 'Не определено',
}

const blockLabel = (code?: string | null) => (code ? BLOCK_LABELS[code] || code : '—')

const HEALTH_LABELS: Record<string, { label: string; color: string }> = {
    healthy: { label: 'Работает', color: 'green' },
    temporarily_blocked: { label: 'Временно заблокирован', color: 'orange' },
    send_blocked: { label: 'Нельзя писать новым', color: 'orange' },
    blocked: { label: 'Заблокирован', color: 'red' },
    banned: { label: 'Забанен', color: 'red' },
    error: { label: 'Ошибка', color: 'red' },
    unknown: { label: 'Не проверялся', color: 'default' },
}

const accountName = (row: AccountStatsRow) =>
    row.name || (row.username ? `@${row.username}` : row.phone)

const num = (value?: number | null) => (value === null || value === undefined ? '—' : value)
const moment = (value?: string | null) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—')

export default function AccountsStatistics() {
    const [data, setData] = useState<StatsPayload | null>(null)
    const [loading, setLoading] = useState(false)
    const [days, setDays] = useState(30)
    const [events, setEvents] = useState<Record<number, AccountBlockEvent[]>>({})
    // Вид и отборы. Отборы уходят на сервер: сводка, график и таблица должны
    // считаться по одной и той же выборке.
    const [view, setView] = useState<'chart' | 'table'>('chart')
    const [geo, setGeo] = useState<string | undefined>(undefined)
    const [temperature, setTemperature] = useState<string | undefined>(undefined)
    const [health, setHealth] = useState<'all' | 'ok' | 'blocked'>('all')
    const [search, setSearch] = useState('')

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const response = await accountsApi.getStatistics({ days, geo, temperature, health })
            setData(response.data)
        } catch {
            message.error('Не удалось загрузить статистику аккаунтов')
        } finally {
            setLoading(false)
        }
    }, [days, geo, temperature, health])

    useEffect(() => {
        load()
    }, [load])

    // История блокировок тянется только для раскрытого аккаунта: строк там
    // бывает много, а нужны они по одному аккаунту за раз.
    const loadEvents = async (accountId: number) => {
        if (events[accountId]) return
        try {
            const response = await accountsApi.getBlockEvents(accountId)
            setEvents((current) => ({ ...current, [accountId]: response.data }))
        } catch {
            message.error('Не удалось загрузить историю блокировок')
        }
    }

    const summary = data?.summary
    const rows = useMemo(() => {
        const all = data?.accounts || []
        const query = search.trim().toLowerCase()
        if (!query) return all
        return all.filter((row) => [row.phone, row.username, row.name]
            .filter(Boolean).join(' ').toLowerCase().includes(query))
    }, [data, search])

    const columns = [
        {
            title: 'Аккаунт',
            key: 'account',
            fixed: 'left' as const,
            width: 210,
            render: (_: unknown, row: AccountStatsRow) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{accountName(row)}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.phone}
                        {row.pool_geo ? ` · ${row.pool_geo}` : ''}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Состояние',
            key: 'health',
            width: 180,
            render: (_: unknown, row: AccountStatsRow) => {
                const health = HEALTH_LABELS[row.health_status || 'unknown'] || HEALTH_LABELS.unknown
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={health.color}>{health.label}</Tag>
                        {row.block_type && (
                            <Text type="secondary" style={{ fontSize: 12 }}>{blockLabel(row.block_type)}</Text>
                        )}
                    </Space>
                )
            },
        },
        {
            title: <Tooltip title="Сколько дней аккаунт подключён к Оркестратору">Живёт, дней</Tooltip>,
            dataIndex: 'alive_days',
            key: 'alive_days',
            width: 110,
            sorter: (a: AccountStatsRow, b: AccountStatsRow) => (a.alive_days || 0) - (b.alive_days || 0),
            render: (value: number | null) => num(value),
        },
        {
            title: 'Отправлено',
            dataIndex: 'sent_total',
            key: 'sent_total',
            width: 120,
            sorter: (a: AccountStatsRow, b: AccountStatsRow) => a.sent_total - b.sent_total,
            defaultSortOrder: 'descend' as const,
        },
        {
            title: `За ${data?.days || days} дн.`,
            dataIndex: 'sent_period',
            key: 'sent_period',
            width: 110,
            sorter: (a: AccountStatsRow, b: AccountStatsRow) => a.sent_period - b.sent_period,
        },
        {
            title: <Tooltip title="Отправлено сегодня, с 00:00 по московскому времени">Сегодня</Tooltip>,
            dataIndex: 'sent_today',
            key: 'sent_today',
            width: 100,
            sorter: (a: AccountStatsRow, b: AccountStatsRow) => a.sent_today - b.sent_today,
        },
        {
            title: <Tooltip title="Отправлено вчера, с 00:00 до 23:59 по московскому времени">Вчера</Tooltip>,
            dataIndex: 'sent_yesterday',
            key: 'sent_yesterday',
            width: 100,
            sorter: (a: AccountStatsRow, b: AccountStatsRow) => a.sent_yesterday - b.sent_yesterday,
        },
        {
            title: <Tooltip title="Скольким разным лидам аккаунт написал">Лидов</Tooltip>,
            dataIndex: 'leads_total',
            key: 'leads_total',
            width: 100,
            sorter: (a: AccountStatsRow, b: AccountStatsRow) => a.leads_total - b.leads_total,
        },
        {
            title: <Tooltip title="Среднее число сообщений в день за всю жизнь аккаунта">В день</Tooltip>,
            dataIndex: 'sent_per_day',
            key: 'sent_per_day',
            width: 100,
            sorter: (a: AccountStatsRow, b: AccountStatsRow) => a.sent_per_day - b.sent_per_day,
        },
        {
            title: 'Блокировок',
            dataIndex: 'blocks_count',
            key: 'blocks_count',
            width: 120,
            sorter: (a: AccountStatsRow, b: AccountStatsRow) => a.blocks_count - b.blocks_count,
            render: (value: number) => value ? <Tag color={value > 2 ? 'red' : 'orange'}>{value}</Tag> : <Tag color="green">нет</Tag>,
        },
        {
            title: <Tooltip title="Сколько аккаунт в среднем успевает отправить до очередной блокировки">До блокировки</Tooltip>,
            key: 'before_block',
            width: 200,
            render: (_: unknown, row: AccountStatsRow) => (
                row.blocks_count ? (
                    <Space direction="vertical" size={0}>
                        <Text>{num(row.avg_sent_before_block)} сообщ. · {num(row.avg_leads_before_block)} лидов</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>в среднем за {num(row.avg_days_before_block)} дн.</Text>
                    </Space>
                ) : <Text type="secondary">не блокировался</Text>
            ),
        },
        {
            title: 'Последняя блокировка',
            key: 'last_block',
            width: 240,
            render: (_: unknown, row: AccountStatsRow) => (
                row.last_block_at ? (
                    <Space direction="vertical" size={0}>
                        <Text>{blockLabel(row.last_block_type)}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {moment(row.last_block_at)} · до неё {num(row.last_block_sent)} сообщ.
                        </Text>
                    </Space>
                ) : <Text type="secondary">—</Text>
            ),
        },
    ]

    const eventColumns = [
        { title: 'Когда', dataIndex: 'occurred_at', key: 'occurred_at', width: 150, render: moment },
        {
            title: 'Блокировка',
            dataIndex: 'block_type',
            key: 'block_type',
            width: 200,
            render: (value: string, row: AccountBlockEvent) => (
                <Space size={6}>
                    <Text>{blockLabel(value)}</Text>
                    {!row.is_block && <Tag>не блокировка</Tag>}
                </Space>
            ),
        },
        { title: 'Отправлено до неё', dataIndex: 'messages_sent', key: 'messages_sent', width: 150 },
        { title: 'Лидов до неё', dataIndex: 'unique_leads', key: 'unique_leads', width: 130 },
        {
            title: 'Отрезок',
            key: 'span',
            width: 200,
            render: (_: unknown, row: AccountBlockEvent) => (
                row.span_started_at
                    ? `${dayjs(row.occurred_at).diff(dayjs(row.span_started_at), 'day')} дн. с ${dayjs(row.span_started_at).format('DD.MM.YYYY')}`
                    : '—'
            ),
        },
        {
            title: 'Снята',
            dataIndex: 'resolved_at',
            key: 'resolved_at',
            width: 150,
            render: (value: string | null) => value ? moment(value) : <Tag color="orange">держится</Tag>,
        },
        { title: 'Причина', dataIndex: 'reason', key: 'reason', ellipsis: true },
    ]

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center" wrap>
                <div>
                    <Title level={4} style={{ margin: 0 }}>Статистика работы аккаунтов</Title>
                    <Text type="secondary">
                        Сколько аккаунты отрабатывают и что их останавливает. Показатели до блокировки
                        сохраняются в момент события, поэтому чистка переписки картину не меняет.
                    </Text>
                </div>
                <Space>
                    <Segmented
                        value={view}
                        onChange={(value) => setView(value as 'chart' | 'table')}
                        options={[
                            { value: 'chart', label: 'График' },
                            { value: 'table', label: 'Таблица' },
                        ]}
                    />
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                </Space>
            </Space>

            {/* Отборы одной строкой над содержимым: период, гео, температура
                пула и состояние. Они уходят на сервер, поэтому сводка, график и
                таблица всегда показывают одну и ту же выборку. */}
            <Space wrap size={12}>
                <Segmented
                    value={days}
                    onChange={(value) => setDays(Number(value))}
                    options={[
                        { value: 7, label: 'Неделя' },
                        { value: 30, label: 'Месяц' },
                        { value: 90, label: '3 месяца' },
                    ]}
                />
                <Select
                    allowClear
                    placeholder="Все гео"
                    style={{ width: 180 }}
                    value={geo}
                    onChange={setGeo}
                    options={(data?.filters?.geos || []).map((item) => ({ value: item, label: item }))}
                />
                <Select
                    allowClear
                    placeholder="Любая температура"
                    style={{ width: 190 }}
                    value={temperature}
                    onChange={setTemperature}
                    options={[
                        { value: 'cold', label: 'Холодный пул' },
                        { value: 'warm', label: 'Тёплый пул' },
                        { value: 'hot', label: 'Горячий пул' },
                    ]}
                />
                <Segmented
                    value={health}
                    onChange={(value) => setHealth(value as 'all' | 'ok' | 'blocked')}
                    options={[
                        { value: 'all', label: 'Все' },
                        { value: 'ok', label: 'Работают' },
                        { value: 'blocked', label: 'Заблокированы' },
                    ]}
                />
                {view === 'table' && (
                    <Input.Search
                        allowClear
                        placeholder="Поиск по номеру или имени"
                        style={{ width: 260 }}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                )}
                {(geo || temperature || health !== 'all' || search) && (
                    <Button type="text" onClick={() => { setGeo(undefined); setTemperature(undefined); setHealth('all'); setSearch('') }}>
                        Сбросить
                    </Button>
                )}
            </Space>

            <Spin spinning={loading}>
                <Row gutter={[12, 12]}>
                    <Col xs={12} md={6}>
                        <Card size="small">
                            <Statistic title="Аккаунтов" value={summary?.accounts_total ?? 0} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                активных {summary?.accounts_active ?? 0} · заблокировано сейчас {summary?.accounts_blocked_now ?? 0}
                            </Text>
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card size="small">
                            <Statistic title="Отправлено сообщений" value={summary?.sent_total ?? 0} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                за период {summary?.sent_period ?? 0} · лидов {summary?.leads_total ?? 0}
                            </Text>
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card size="small">
                            <Statistic title="Блокировок" value={summary?.blocks_total ?? 0} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                за период {summary?.blocks_period ?? 0} · без блокировок {summary?.accounts_never_blocked ?? 0} акк.
                            </Text>
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="В среднем до блокировки"
                                value={summary?.avg_sent_before_block ?? 0}
                                suffix="сообщ."
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {num(summary?.avg_leads_before_block)} лидов · {num(summary?.avg_days_before_block)} дн. работы
                            </Text>
                        </Card>
                    </Col>
                </Row>

                {view === 'chart' && data && (
                    <div style={{ marginTop: 16 }}>
                        <AccountsCharts data={data} labelOf={blockLabel} />
                    </div>
                )}

                {view === 'table' && (
                <Table
                    style={{ marginTop: 16 }}
                    rowKey="id"
                    size="small"
                    columns={columns}
                    dataSource={rows}
                    scroll={{ x: 1700 }}
                    pagination={{ pageSize: 25, hideOnSinglePage: true }}
                    expandable={{
                        onExpand: (expanded, row) => { if (expanded) loadEvents(row.id) },
                        expandedRowRender: (row: AccountStatsRow) => (
                            events[row.id]?.length ? (
                                <Table
                                    rowKey="id"
                                    size="small"
                                    columns={eventColumns}
                                    dataSource={events[row.id]}
                                    pagination={false}
                                    scroll={{ x: 1000 }}
                                />
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Блокировок не было" />
                            )
                        ),
                    }}
                    locale={{ emptyText: <Empty description="Аккаунтов пока нет" /> }}
                />
                )}

                {view === 'table' && Boolean(data?.block_types?.length) && (
                    <Card size="small" style={{ marginTop: 16 }} title="Из-за чего блокируются">
                        <Table
                            rowKey="block_type"
                            size="small"
                            pagination={false}
                            dataSource={data?.block_types}
                            columns={[
                                { title: 'Тип блокировки', dataIndex: 'block_type', key: 'block_type', render: blockLabel },
                                { title: 'Случаев', dataIndex: 'count', key: 'count', width: 120 },
                                {
                                    title: 'В среднем сообщений до неё',
                                    dataIndex: 'avg_sent_before',
                                    key: 'avg_sent_before',
                                    width: 220,
                                    render: (value: number | null) => num(value),
                                },
                                { title: 'Последний раз', dataIndex: 'last_at', key: 'last_at', width: 170, render: moment },
                            ]}
                        />
                    </Card>
                )}
            </Spin>
        </Space>
    )
}
